import { provideAuth } from "app/modules/auth/services";
import { PaginatorI18n, provideIcons } from "app/shared/services";
import { TranslocoHttpLoader } from "app/shared/utils";
import { provideEnvironmentNgxMask } from "ngx-mask";
import { firstValueFrom } from "rxjs";

import { registerLocaleData } from "@angular/common";
import { provideHttpClient } from "@angular/common/http";
import localePT from "@angular/common/locales/pt";
import { APP_INITIALIZER, ApplicationConfig, inject, LOCALE_ID } from "@angular/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { provideAnimations } from "@angular/platform-browser/animations";
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from "@angular/router";
import { provideFuse } from "@fuse";
import { provideTransloco, TranslocoService } from "@jsverse/transloco";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import { appRoutes } from "./app.routes";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { LuxonDateAdapter } from "@angular/material-luxon-adapter";

registerLocaleData(localePT);

export const MY_FORMATS = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(),
    provideEnvironmentNgxMask(),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" })
    ),

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "DD/MM/YYYY",
        },
        display: {
          dateInput: "DD/MM/YYYY",
          monthYearLabel: "MMM YYYY",
          dateA11yLabel: "LL",
          monthYearA11yLabel: "MMMM YYYY",
        },
      },
    },
    { provide: LOCALE_ID, useValue: "pt-br" },
    provideMomentDateAdapter(MY_FORMATS),
    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          // {
          //   id: "en",
          //   label: "English",
          // },
          {
            id: "pt",
            label: "Português",
          },
        ],
        defaultLang: "pt",
        fallbackLang: "en",
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    {
      // Preload the default language before the app starts to prevent empty/jumping content
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translocoService = inject(TranslocoService);

        const savedLanguage = localStorage.getItem("lang");
        const defaultLang = translocoService.getDefaultLang();
        translocoService.setActiveLang(savedLanguage ?? defaultLang);

        return () => firstValueFrom(translocoService.load(savedLanguage ?? defaultLang));
      },
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      deps: [TranslocoService],
      useFactory: (translateService: TranslocoService) => new PaginatorI18n(translateService).getPaginatorIntl(),
    },

    // Fuse
    provideAuth(),
    provideIcons(),
    provideFuse({
      // mockApi: {
      //   delay: 0,
      //   services: mockApiServices,
      // },
      fuse: {
        layout: "modern",
        scheme: "light",
        screens: {
          sm: "600px",
          md: "960px",
          lg: "1280px",
          xl: "1440px",
        },
        theme: "theme-default",
        themes: [
          {
            id: "theme-default",
            name: "Default",
          },
          {
            id: "theme-brand",
            name: "Brand",
          },
          {
            id: "theme-teal",
            name: "Teal",
          },
          {
            id: "theme-rose",
            name: "Rose",
          },
          {
            id: "theme-purple",
            name: "Purple",
          },
          {
            id: "theme-amber",
            name: "Amber",
          },
          {
            id: "theme-pink",
            name: "Pink",
          },
        ],
      },
    }),
  ],
};
